import * as React from 'react';

import {
  createStyles,
  Grid,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core';

import { ILieuucationPropertiesShortTermRentals } from '@lieuu/core';

import BarChart from '../charts/BarChart';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('lg')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6)
      }
    },
    grid: { width: '100%' },
    divider: {
      height: '100%'
    }
  });

type IProps = WithStyles<typeof styles> & {
  properties: ILieuucationPropertiesShortTermRentals;
  cbsaProperties: ILieuucationPropertiesShortTermRentals;
};

const ShortTermRentals: React.FC<IProps> = ({
  classes,
  properties,
  cbsaProperties
}) => {
  return (
    <div className={classes.root}>
      <Grid
        alignItems="center"
        justify="center"
        spacing={2}
        className={classes.grid}
        container
      >
        <BarChart
          title="Airbnb Nightly Rates"
          variant="number"
          format="dollar"
          data={[
            {
              id: 'Room',
              lieuucation: properties.airbnb.nightly.roomRate,
              cbsa: cbsaProperties.airbnb.nightly.roomRate
            },
            {
              id: 'Whole Home',
              lieuucation: properties.airbnb.nightly.wholeHomeRate,
              cbsa: cbsaProperties.airbnb.nightly.wholeHomeRate
            }
          ]}
          description={
            <>
              {
                'This is based on the asking price for rentals in December 2018 on Airbnb.com.'
              }
              <br />
              <b>
                {
                  ' Important: if there are no listings in the area, no data is shown. This does not mean that the area is not suitable for short-term rentals.'
                }
              </b>
              <br />
              <i>
                {
                  ' We are constantly adding to our short-term rentals data. Stay tuned for further developments!'
                }
              </i>
            </>
          }
        />

        <BarChart
          title="Airbnb Weekly Rates"
          variant="number"
          format="dollar"
          data={[
            {
              id: 'Room',
              lieuucation: properties.airbnb.weekly.roomRate,
              cbsa: cbsaProperties.airbnb.weekly.roomRate
            },
            {
              id: 'Whole Home',
              lieuucation: properties.airbnb.weekly.wholeHomeRate,
              cbsa: cbsaProperties.airbnb.weekly.wholeHomeRate
            }
          ]}
          description={
            <>
              {
                'This is based on the asking price for rentals in December 2018 on Airbnb.com, with a weekly discount price factor included.'
              }
              <br />
              <b>
                {
                  ' Important: if there are no listings in the area, no data is shown. This does not mean that the area is not suitable for short-term rentals.'
                }
              </b>
              <br />
              <i>
                {
                  ' We are constantly adding to our short-term rentals data. Stay tuned for further developments!'
                }
              </i>
            </>
          }
        />

        <BarChart
          title="Airbnb Monthly Rates"
          variant="number"
          format="dollar"
          data={[
            {
              id: 'Room',
              lieuucation: properties.airbnb.nightly.roomRate * 30 * 0.75,
              cbsa: cbsaProperties.airbnb.nightly.roomRate * 30 * 0.75
            },
            {
              id: 'Whole Home',
              lieuucation: properties.airbnb.nightly.wholeHomeRate * 30 * 0.75,
              cbsa: cbsaProperties.airbnb.nightly.wholeHomeRate * 30 * 0.75
            }
          ]}
          description={
            <>
              {
                'This is based on the asking price for rentals in December 2018 on Airbnb.com, with a 75% occupancy rate factored in.'
              }
              <br />
              <b>
                {
                  ' Important: if there are no listings in the area, no data is shown. This does not mean that the area is not suitable for short-term rentals.'
                }
              </b>
              <br />
              <b>
                {
                  ' This assumes a 75% occupancy rate. This is highly variable depending on season and location.'
                }
              </b>
              <br />
              <i>
                {
                  ' We are constantly adding to our short-term rentals data. Stay tuned for further developments!'
                }
              </i>
            </>
          }
        />
      </Grid>
    </div>
  );
};

export default withStyles(styles)(ShortTermRentals);
